import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, Smartphone, BellOff, Trash2 } from 'lucide-react';
import { useWebPush } from '../../hooks/useWebPush';
import {
  NotificationPreferences, NOTIFICATION_CATEGORIES, LIVE_NOTIFICATION_CATEGORIES, NOTIFICATION_CATEGORY_LABELS, NOTIFICATION_CHANNELS,
  getNotificationPreferences, updateNotificationPreferences, getNotificationChannelsStatus,
} from '../../composables/notificationAPI';

const CHANNEL_LABELS: Record<string, string> = { IN_APP: 'ในเว็บ', WEB_PUSH: 'นอกเว็บ', TELEGRAM: 'Telegram' };

const EMPTY_PREFS: NotificationPreferences = {
  channels: { IN_APP: true, WEB_PUSH: false, TELEGRAM: false },
  // default ตาม backend: ทุกหมวดเปิดทุกช่องทาง ยกเว้น ARTICLE/TELEGRAM
  categories: Object.fromEntries(NOTIFICATION_CATEGORIES.map((c) => [c, { IN_APP: true, WEB_PUSH: true, TELEGRAM: c !== 'ARTICLE' }])) as any,
  quietHours: { enabled: false, start: '22:00', end: '08:00', timezone: 'Asia/Bangkok' },
  locale: 'th',
};

const NotificationSettings: React.FC = () => {
  const push = useWebPush();
  const [prefs, setPrefs] = useState<NotificationPreferences>(EMPTY_PREFS);
  const [channelsStatus, setChannelsStatus] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<string>>(new Set());
  const [removingDevices, setRemovingDevices] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setPrefs(await getNotificationPreferences());
      } catch (err: any) {
        toast.error(err.message || 'โหลดการตั้งค่าไม่สำเร็จ');
      } finally {
        setLoading(false);
      }
    })();
    getNotificationChannelsStatus().then(setChannelsStatus).catch(() => {});
    void push.refreshDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateNotificationPreferences(prefs);
      toast.success('บันทึกการตั้งค่าแล้ว');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const togglePushDevice = async () => {
    try {
      if (push.subscribed) {
        await push.disable();
        toast.success('ปิด แจ้งเตือน  บนอุปกรณ์นี้แล้ว');
      } else {
        await push.enable();
        toast.success('เปิด แจ้งเตือน  บนอุปกรณ์นี้แล้ว');
      }
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const toggleDeviceSelection = (id: string, checked: boolean) => {
    setSelectedDeviceIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const handleRemoveSelectedDevices = async () => {
    if (selectedDeviceIds.size === 0) return;
    setRemovingDevices(true);
    try {
      await push.removeDevices(Array.from(selectedDeviceIds));
      setSelectedDeviceIds(new Set());
      toast.success('ลบอุปกรณ์ที่เลือกแล้ว');
    } catch (err: any) {
      toast.error(err.message || 'ลบอุปกรณ์ไม่สำเร็จ');
    } finally {
      setRemovingDevices(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-10 opacity-70"><Loader2 className="animate-spin" size={20} /></div>;
  }

  return (
    <div className="overflow-y-auto px-4 py-3 flex flex-col gap-4 text-sm">
      <section>
        <div className="font-semibold mb-2 flex items-center gap-2"><Smartphone size={14} /> แจ้งเตือน  บนอุปกรณ์นี้</div>
        {push.support !== 'ready' ? (
          <p className="text-xs opacity-60 flex items-center gap-1"><BellOff size={12} /> เบราว์เซอร์/หน้านี้ไม่รองรับ Web Push (iOS ต้อง Add to Home Screen ก่อน)</p>
        ) : (
          <button
            onClick={togglePushDevice}
            disabled={push.busy}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors duration-150 active:scale-95"
            style={{ backgroundColor: push.subscribed ? 'var(--red-text-color-btn, #ef4444)' : 'var(--button-create-bg)', color: push.subscribed ? '#fff' : 'var(--button-text-color)' }}
          >
            {push.busy ? 'กำลังดำเนินการ...' : push.subscribed ? 'ปิด แจ้งเตือน  ที่อุปกรณ์นี้' : 'เปิด แจ้งเตือน  ที่อุปกรณ์นี้'}
          </button>
        )}
      </section>

      {push.devices.length > 0 && (
        <section>
          <div className="font-semibold mb-2">อุปกรณ์ที่เปิด Push ไว้ ({push.devices.length})</div>
          <div className="flex flex-col gap-1.5 mb-2">
            {push.devices.map((d) => (
              <label
                key={d.id}
                className="flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg cursor-pointer"
                style={{ backgroundColor: 'var(--bg-item)' }}
              >
                <input
                  type="checkbox"
                  checked={selectedDeviceIds.has(d.id)}
                  onChange={(e) => toggleDeviceSelection(d.id, e.target.checked)}
                />
                <span className="flex-1 truncate">{d.device || d.id}</span>
                {d.id === push.currentDeviceId && (
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold shrink-0"
                    style={{ backgroundColor: 'var(--button-create-bg)', color: 'var(--button-text-color)' }}
                  >
                    อุปกรณ์นี้
                  </span>
                )}
              </label>
            ))}
          </div>
          <button
            onClick={handleRemoveSelectedDevices}
            disabled={selectedDeviceIds.size === 0 || removingDevices}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors duration-150 active:scale-95 disabled:opacity-40"
            style={{ backgroundColor: 'var(--red-text-color-btn, #ef4444)', color: '#fff' }}
          >
            <Trash2 size={12} />
            {removingDevices ? 'กำลังลบ...' : `ลบอุปกรณ์ที่เลือก (${selectedDeviceIds.size})`}
          </button>
        </section>
      )}

      <section>
        <div className="font-semibold mb-2">ช่องทางหลัก</div>
        <div className="flex gap-3 flex-wrap">
          {NOTIFICATION_CHANNELS.map((ch) => {
            const available = channelsStatus?.[ch]?.available !== false;
            return (
              <label key={ch} className="flex items-center gap-1 text-xs" title={!available ? 'เซิร์ฟเวอร์ยังไม่เปิดใช้งานช่องทางนี้' : undefined}>
                <input
                  type="checkbox"
                  disabled={!available}
                  checked={prefs.channels[ch]}
                  onChange={(e) => setPrefs((p) => ({ ...p, channels: { ...p.channels, [ch]: e.target.checked } }))}
                />
                {CHANNEL_LABELS[ch]}
                {!available && <span className="opacity-50">(ยังไม่เปิดใช้งาน)</span>}
                {ch === 'TELEGRAM' && available && channelsStatus?.TELEGRAM?.linked === false && (
                  <span className="opacity-50">(ยังไม่ผูกบัญชี)</span>
                )}
              </label>
            );
          })}
        </div>
      </section>

      <section>
        <div className="font-semibold mb-2">แจ้งเตือนตามหมวด</div>
        <div className="overflow-x-auto -mx-1">
          <table className="text-[11px] w-full">
            <thead>
              <tr className="opacity-60">
                <th className="text-left font-normal px-1 py-1">หมวด</th>
                {NOTIFICATION_CHANNELS.map((ch) => <th key={ch} className="font-normal px-1 py-1">{CHANNEL_LABELS[ch]}</th>)}
              </tr>
            </thead>
            <tbody>
              {/* ซ่อนหมวดที่ยังไม่มี type — ค่าของหมวดเหล่านั้นยังถูกส่งกลับตามที่โหลดมา ไม่หาย */}
              {LIVE_NOTIFICATION_CATEGORIES.map((cat) => (
                <tr key={cat} className="border-t" style={{ borderColor: 'var(--bg-item)' }}>
                  <td className="px-1 py-1">{NOTIFICATION_CATEGORY_LABELS[cat]}</td>
                  {NOTIFICATION_CHANNELS.map((ch) => (
                    <td key={ch} className="text-center px-1 py-1">
                      <input
                        type="checkbox"
                        checked={prefs.categories[cat]?.[ch] ?? false}
                        onChange={(e) => setPrefs((p) => ({
                          ...p,
                          categories: { ...p.categories, [cat]: { ...p.categories[cat], [ch]: e.target.checked } },
                        }))}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* <section>
        <div className="font-semibold mb-2">ช่วงงดรบกวน (Push / Telegram)</div>
        <label className="flex items-center gap-2 text-xs mb-2">
          <input
            type="checkbox"
            checked={prefs.quietHours.enabled}
            onChange={(e) => setPrefs((p) => ({ ...p, quietHours: { ...p.quietHours, enabled: e.target.checked } }))}
          />
          เปิดใช้งาน
        </label>
        {prefs.quietHours.enabled && (
          <div className="flex items-center gap-2 text-xs">
            <input
              type="time"
              value={prefs.quietHours.start}
              onChange={(e) => setPrefs((p) => ({ ...p, quietHours: { ...p.quietHours, start: e.target.value } }))}
              className="rounded px-1 py-0.5"
              style={{ backgroundColor: 'var(--bg-item)', color: 'var(--text-color)' }}
            />
            <span>ถึง</span>
            <input
              type="time"
              value={prefs.quietHours.end}
              onChange={(e) => setPrefs((p) => ({ ...p, quietHours: { ...p.quietHours, end: e.target.value } }))}
              className="rounded px-1 py-0.5"
              style={{ backgroundColor: 'var(--bg-item)', color: 'var(--text-color)' }}
            />
          </div>
        )}
      </section> */}

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full py-2 rounded-lg font-semibold text-sm transition-colors duration-150 active:scale-95"
        style={{ backgroundColor: 'var(--button-create-bg)', color: 'var(--button-text-color)' }}
      >
        {saving ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า'}
      </button>
    </div>
  );
};

export default NotificationSettings;
